import React, { useState } from 'react'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Progress } from '../ui/progress'
import { Separator } from '../ui/separator'
import { Alert, AlertDescription } from '../ui/alert'
import { 
  Sparkles,
  Image as ImageIcon,
  Download,
  Loader2,
  CreditCard,
  Eye,
  RefreshCw,
  Share2,
  Wand2,
  Palette,
  Type,
  Layout,
  Zap
} from 'lucide-react'
import { mockAI, AIAssetRequest, AIAssetResult } from '../../utils/mockAI'

interface AIAssetGeneratorProps {
  brandData: {
    name: string
    industry?: string
    colors?: string[]
    fonts?: string[]
    tone?: string
    values?: string[]
  }
  onAssetGenerated?: (asset: AIAssetResult) => void
  className?: string
}

export function AIAssetGenerator({ brandData, onAssetGenerated, className = '' }: AIAssetGeneratorProps) {
  const [assetType, setAssetType] = useState<string>('')
  const [specifications, setSpecifications] = useState({
    dimensions: '',
    format: 'PNG',
    style: 'Modern',
    content: ''
  })
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedAsset, setGeneratedAsset] = useState<AIAssetResult | null>(null)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState('')

  const assetTypes = [
    { value: 'logo', label: 'Logo Variations', icon: '🎨', description: 'Different styles and formats of your logo' },
    { value: 'business_card', label: 'Business Cards', icon: '💼', description: 'Professional business card designs' },
    { value: 'social_post', label: 'Social Media Posts', icon: '📱', description: 'Instagram, LinkedIn, Twitter templates' },
    { value: 'presentation', label: 'Presentation Templates', icon: '📊', description: 'Slide deck templates with your branding' },
    { value: 'email_header', label: 'Email Headers', icon: '📧', description: 'Professional email signature headers' }
  ]

  const styleOptions = [
    'Modern', 'Minimalist', 'Bold', 'Classic', 'Creative', 'Professional', 'Playful', 'Elegant'
  ]

  const formatOptions = [
    'PNG', 'JPG', 'SVG', 'PDF'
  ]

  const generateAsset = async () => {
    if (!assetType) return

    setIsGenerating(true)
    setProgress(0)
    setCurrentStep('Preparing AI generation...')

    try {
      // Simulate generation steps
      const steps = [
        { step: 'Analyzing brand context...', duration: 1000 },
        { step: 'Generating creative concepts...', duration: 2000 },
        { step: 'Applying brand guidelines...', duration: 1500 },
        { step: 'Creating variations...', duration: 2000 },
        { step: 'Optimizing quality...', duration: 1000 },
        { step: 'Finalizing assets...', duration: 500 }
      ]

      for (let i = 0; i < steps.length; i++) {
        setCurrentStep(steps[i].step)
        setProgress((i + 1) / steps.length * 90)
        await new Promise(resolve => setTimeout(resolve, steps[i].duration))
      }

      const request: AIAssetRequest = {
        type: assetType as any,
        brandContext: {
          name: brandData.name,
          industry: brandData.industry || 'Technology',
          colors: brandData.colors || ['#2563EB', '#1E40AF'],
          fonts: brandData.fonts || ['Inter'],
          tone: brandData.tone || 'Professional',
          values: brandData.values || []
        },
        specifications
      }

      const result = await mockAI.generateAsset(request)
      
      setProgress(100)
      setCurrentStep('Generation complete!')
      setGeneratedAsset(result)
      onAssetGenerated?.(result)

    } catch (error) {
      console.error('Asset generation failed:', error)
      setCurrentStep('Generation failed - please try again')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleDownload = (url: string, format: string) => {
    // In a real app, this would trigger an actual download
    console.log(`Downloading ${format} from ${url}`)
  }

  const resetGenerator = () => {
    setGeneratedAsset(null)
    setProgress(0)
    setCurrentStep('')
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Asset Type Selection */}
      {!generatedAsset && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5" />
              AI Asset Generator
            </CardTitle>
            <CardDescription>
              Generate professional marketing materials using AI, powered by your brand guidelines
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <Label>What would you like to create?</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {assetTypes.map((type) => (
                  <div
                    key={type.value}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      assetType === type.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setAssetType(type.value)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{type.icon}</span>
                      <div>
                        <div className="font-medium">{type.label}</div>
                        <div className="text-sm text-slate-600">{type.description}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {assetType && (
              <>
                <Separator />
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="style">Style</Label>
                    <Select value={specifications.style} onValueChange={(value) => 
                      setSpecifications(prev => ({ ...prev, style: value }))
                    }>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {styleOptions.map((style) => (
                          <SelectItem key={style} value={style}>{style}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="format">Format</Label>
                    <Select value={specifications.format} onValueChange={(value) => 
                      setSpecifications(prev => ({ ...prev, format: value }))
                    }>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {formatOptions.map((format) => (
                          <SelectItem key={format} value={format}>{format}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dimensions">Dimensions</Label>
                    <Input
                      id="dimensions"
                      placeholder="e.g., 1080x1080"
                      value={specifications.dimensions}
                      onChange={(e) => 
                        setSpecifications(prev => ({ ...prev, dimensions: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content">Content Instructions (Optional)</Label>
                  <Textarea
                    id="content"
                    placeholder="Describe any specific content, text, or elements you want included..."
                    value={specifications.content}
                    onChange={(e) => 
                      setSpecifications(prev => ({ ...prev, content: e.target.value }))
                    }
                    rows={3}
                  />
                </div>

                <Alert>
                  <Sparkles className="w-4 h-4" />
                  <AlertDescription>
                    Your brand colors ({brandData.colors?.join(', ') || 'default colors'}) and fonts 
                    will be automatically applied to maintain consistency.
                  </AlertDescription>
                </Alert>

                <Button 
                  onClick={generateAsset}
                  disabled={isGenerating}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Asset
                    </>
                  )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Generation Progress */}
      {isGenerating && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Creating Your Asset
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{currentStep}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
            
            <Alert>
              <Sparkles className="w-4 h-4" />
              <AlertDescription>
                Our AI is creating multiple variations tailored to your brand. This process usually takes 30-90 seconds.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Generated Asset Results */}
      {generatedAsset && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  Your Generated Asset
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    {Math.round(generatedAsset.metadata.confidence * 100)}% Quality
                  </Badge>
                  <Button variant="outline" size="sm" onClick={resetGenerator}>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Generate New
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Main Asset Display */}
              <div className="mb-6">
                <div className="relative aspect-video bg-slate-100 rounded-lg overflow-hidden mb-4">
                  <img 
                    src={generatedAsset.url} 
                    alt="Generated asset"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2">
                    <Button size="sm" variant="secondary">
                      <Eye className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{generatedAsset.metadata.style} {generatedAsset.type.replace('_', ' ')}</p>
                    <p className="text-sm text-slate-600">
                      {generatedAsset.metadata.dimensions} • {generatedAsset.metadata.format}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm">
                      <Share2 className="w-4 h-4 mr-1" />
                      Share
                    </Button>
                    <Button size="sm" className="bg-gradient-to-r from-blue-600 to-purple-600">
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              </div>

              {/* Variations */}
              <div>
                <h4 className="font-medium mb-3">Alternative Variations</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {generatedAsset.variations.map((variation) => (
                    <div key={variation.id} className="border rounded-lg p-3">
                      <div className="aspect-video bg-slate-100 rounded mb-2 overflow-hidden">
                        <img 
                          src={variation.url} 
                          alt={variation.description}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <p className="text-sm font-medium mb-1">{variation.description}</p>
                      <Button variant="outline" size="sm" className="w-full">
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Download Options */}
              <Separator className="my-6" />
              
              <div>
                <h4 className="font-medium mb-3">Download Formats</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(generatedAsset.downloadUrls).map(([format, url]) => (
                    <Button 
                      key={format}
                      variant="outline" 
                      onClick={() => handleDownload(url, format.toUpperCase())}
                      className="justify-start"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {format.toUpperCase()}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upgrade Prompt for Free Users */}
          <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium mb-1">Unlock Unlimited AI Generation</h4>
                  <p className="text-sm text-slate-600 mb-3">
                    Generate unlimited high-resolution assets, remove watermarks, and access premium templates.
                  </p>
                  <Button size="sm" className="bg-gradient-to-r from-purple-600 to-blue-600">
                    <CreditCard className="w-4 h-4 mr-1" />
                    Upgrade to Pro
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}